import { supabase } from '../lib/supabase';
import { webhookIntegrationsService } from '../services/webhookIntegrationsService';

// ─── Tenant Guard ─────────────────────────────────────────────────────────────
// Returns the impersonated user's ID when an admin impersonation session is
// active, otherwise returns the real authenticated user's ID.
const tenantGuard = async () => {
  const impersonationData = sessionStorage?.getItem('superAdminImpersonating');
  if (impersonationData) {
    try {
      const parsed = JSON.parse(impersonationData);
      if (parsed?.targetId) return parsed?.targetId;
    } catch (e) { /* fall through */ }
  }
  const { data: { user }, error } = await supabase?.auth?.getUser();
  if (error || !user) throw new Error('Not authenticated');
  return user?.id;
};

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

// Check if error is schema-related
const isSchemaError = (error) => {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
};

export const submissionsService = {
  // Get submissions for a specific form
  async getSubmissionsByFormId(formId) {
    try {
      const userId = await tenantGuard();

      // Verify user owns the form
      const { data: form } = await supabase?.from('forms')?.select('id')?.eq('id', formId)?.eq('user_id', userId)?.single();

      if (!form) throw new Error('Form not found or access denied');

      const { data, error } = await supabase?.from('form_submissions')?.select('*')?.eq('form_id', formId)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching submissions:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error in getSubmissionsByFormId:', error);
      throw error;
    }
  },

  // Get the number of automated attempts blocked before a submission was saved.
  async getBlockedSubmissionCount(formId) {
    const userId = await tenantGuard();
    const { data: form } = await supabase
      ?.from('forms')
      ?.select('id')
      ?.eq('id', formId)
      ?.eq('user_id', userId)
      ?.single();

    if (!form) throw new Error('Form not found or access denied');

    const { count, error } = await supabase
      ?.from('blocked_form_submissions')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('form_id', formId);

    if (error) {
      if (isSchemaError(error)) return 0;
      throw error;
    }
    return count || 0;
  },

  // Get analytics events for a form
  async getAnalyticsByFormId(formId) {
    try {
      const userId = await tenantGuard();

      // Verify user owns the form
      const { data: form } = await supabase?.from('forms')?.select('id')?.eq('id', formId)?.eq('user_id', userId)?.single();

      if (!form) throw new Error('Form not found or access denied');

      const { data, error } = await supabase?.from('analytics_events')?.select('*')?.eq('form_id', formId)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching analytics:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error in getAnalyticsByFormId:', error);
      throw error;
    }
  },

  // Get all submissions for current user (across all forms)
  async getAllSubmissions() {
    try {
      const userId = await tenantGuard();

      const { data, error } = await supabase
        ?.from('form_submissions')
        ?.select('*, forms!inner(user_id)')
        ?.eq('forms.user_id', userId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching all submissions:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error in getAllSubmissions:', error);
      throw error;
    }
  },

  // Create submission (public endpoint)
  async createSubmission(formId, submissionData) {
    try {
      // Do NOT call .select() after insert — the public INSERT policy allows
      // the write but there is no public SELECT policy, so RETURNING would be
      // rejected by RLS. We only need to know whether the insert succeeded.
      const { error } = await supabase?.from('form_submissions')?.insert({
          form_id: formId,
          submission_data: submissionData,
          status: 'new',
          created_at: new Date()?.toISOString()
        });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error creating submission:', error?.message);
        return null;
      }

      // Trigger webhooks asynchronously (don't wait for them)
      webhookIntegrationsService?.triggerWebhooks(formId, submissionData)?.catch(err => {
        console.error('Webhook trigger failed:', err);
      });

      // Return a confirmation object so callers get a truthy result
      return { formId, submitted: true };
    } catch (error) {
      console.error('Error in createSubmission:', error);
      throw error;
    }
  },

  // Delete a single submission (owner-only)
  async deleteSubmission(submissionId, formId) {
    try {
      const userId = await tenantGuard();

      // Verify user owns the form before deleting
      const { data: form } = await supabase?.from('forms')?.select('id')?.eq('id', formId)?.eq('user_id', userId)?.single();
      if (!form) throw new Error('Form not found or access denied');

      const { error } = await supabase?.from('form_submissions')?.delete()?.eq('id', submissionId)?.eq('form_id', formId);

      if (error) {
        console.error('Error deleting submission:', error?.message);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteSubmission:', error);
      throw error;
    }
  },

  // Subscribe to submissions for a form
  subscribeToSubmissions(formId, callback) {
    const channel = supabase?.channel('submissions_changes')?.on('postgres_changes',
        {
          event: '*',schema: 'public',table: 'form_submissions',
          filter: `form_id=eq.${formId}`
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }
};